/**
 * Learning Center API: image assets for image slides (plan
 * docs/plans/lms-plan.md 5.2 lms_assets, 6.1, D10, D17; the 18.22 gap, closed
 * by WP-74's routes on routes/lms/catalog.js). Typed calls over services/api
 * (the shared axios instance with the JWT and X-Site-Id); nothing under
 * src/lms imports axios (2.3a).
 *
 *   GET  /admin/assets?limit=&offset=  -> { items, page }   (the network library, newest first; admin)
 *   POST /admin/assets (multipart: file, alt_default?) -> 201 the asset        (admin)
 *   GET  /assets/:id/url               -> { url, filename, mime, width, height } (presigned inline, 10 minutes;
 *                                         every entitled person: api/courses.ts getAssetUrl, which the player uses)
 *
 * The FormData is posted with no explicit Content-Type: the browser sets the
 * multipart boundary itself (the external-certificate upload's pattern).
 */
import api from '../../services/api';
import type { LmsList } from './client';
import { getAssetUrl } from './courses';

export { getAssetUrl };

/** 5.2 lms_assets.mime: the upload allow-list (SVG never, D10). */
export const ASSET_MIMES: readonly string[] = ['image/png', 'image/jpeg', 'image/webp'];
/** 2.3a "Uploads": images up to 25 MB (storage.js LMS_MAX_BYTES_BY_KIND.assets). */
export const ASSET_MAX_BYTES = 25 * 1024 * 1024;
/** services/lms/courses/assets.js ASSET_ALT_MAX. */
export const ASSET_ALT_MAX = 300;
/** Appendix A: a list page is at most 200. */
export const ASSETS_MAX_LIMIT = 200;

export interface LmsAsset {
  id: string;
  network_id: string;
  filename: string;
  mime: string;
  size_bytes: number;
  width: number | null;
  height: number | null;
  alt_default: string | null;
  sha256: string;
  uploaded_by: string;
  created_at: string;
}

export interface AssetUrl {
  url: string;
  filename?: string;
  mime?: string;
  width?: number | null;
  height?: number | null;
}

/** GET /api/lms/admin/assets */
export async function listAssets(options: { limit?: number; offset?: number } = {}): Promise<LmsList<LmsAsset>> {
  const params: Record<string, number> = {};
  if (options.limit !== undefined) params.limit = options.limit;
  if (options.offset !== undefined) params.offset = options.offset;
  const response = await api.get<LmsList<LmsAsset>>('/api/lms/admin/assets', { params });
  return response.data;
}

/** The multipart body of POST /admin/assets (alt_default left out when empty). */
export function buildAssetForm(file: File, altDefault?: string | null): FormData {
  const form = new FormData();
  form.append('file', file);
  const alt = typeof altDefault === 'string' ? altDefault.trim() : '';
  if (alt) form.append('alt_default', alt);
  return form;
}

/** POST /api/lms/admin/assets (multipart) -> the stored asset. */
export async function uploadAsset(file: File, altDefault?: string | null): Promise<LmsAsset> {
  const response = await api.post<LmsAsset>('/api/lms/admin/assets', buildAssetForm(file, altDefault));
  return response.data;
}

/**
 * What stops a file before it is sent (the server refuses the same things):
 * null when it may go.
 */
export function assetFileProblem(file: File | null): string | null {
  if (!file) return 'Choose an image.';
  if (!ASSET_MIMES.includes(file.type)) return 'The image must be a PNG, JPEG or WebP file.';
  if (file.size === 0) return 'The file is empty.';
  if (file.size > ASSET_MAX_BYTES) return 'The image is larger than 25 MB.';
  return null;
}

/** A short size label: 820 B, 48 KB, 2.3 MB. */
export function assetSizeLabel(bytes: number | null | undefined): string {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
